import React from 'react';
import { QAItem as QAItemType } from '../../types';
import { typography, colors } from '../../styles/designTokens';

interface QAItemProps {
  item: QAItemType;
  index: number;
  isOpen: boolean;
  onToggle: () => void;
}

const QAItem: React.FC<QAItemProps> = ({ item, index, isOpen, onToggle }) => {
  const [isVisible, setIsVisible] = React.useState(false);
  const itemRef = React.useRef<HTMLDivElement>(null);

  // IntersectionObserver for Staggered Fade-In
  React.useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      {
        threshold: 0.05,
        rootMargin: '200px',
      }
    );

    const currentRef = itemRef.current;
    if (currentRef) {
      observer.observe(currentRef);
    }

    return () => {
      if (currentRef) {
        observer.unobserve(currentRef);
      }
    };
  }, []);

  // קביעת כיוון הכניסה - זוגי מימין, אי-זוגי משמאל
  const isEven = index % 2 === 0;
  const enterFromRight = isEven; // זוגי = מימין, אי-זוגי = משמאל

  return (
    <div
      ref={itemRef}
      className={`qa-item relative overflow-hidden ${
        isOpen ? 'qa-item-open' : ''
      }`}
      style={{
        backgroundColor: '#000000',
        border: `2px solid #FFFFFF`,
        borderRadius: window.innerWidth <= 768 ? '16px' : '24px',
        boxShadow: `4px 4px 15px rgba(0, 0, 0, 0.3)`,
        opacity: isVisible ? 1 : 0,
        transform: window.innerWidth <= 768
          ? (isVisible 
              ? 'translateY(0) scale(1)' 
              : 'translateY(50px) scale(0.95)')
          : (isVisible 
              ? 'translateX(0) translateY(0) rotate(0deg) scale(1)' 
              : enterFromRight 
                ? 'translateX(500px) translateY(20px) rotate(-5deg) scale(0.9)' 
                : 'translateX(-500px) translateY(20px) rotate(5deg) scale(0.9)'),
        // transition: `opacity 0.9s cubic-bezier(0.34, 1.56, 0.64, 1) ${index * 0.15}s, transform 0.9s cubic-bezier(0.34, 1.56, 0.64, 1) ${index * 0.15}s, box-shadow 0.3s ease`,
        transition: `opacity 0.9s cubic-bezier(0.25, 0.46, 0.45, 0.94) ${index * 0.15}s, transform 0.9s cubic-bezier(0.25, 0.46, 0.45, 0.94) ${index * 0.15}s, box-shadow 0.3s ease`,
        willChange: isVisible ? 'auto' : 'transform, opacity',
        pointerEvents: isVisible ? 'auto' : 'none',
        width: '100%',
        maxWidth: '100%',
      }}
    >
      {/* Question Row */}
      <button
        onClick={onToggle}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            onToggle();
          }
        }}
        aria-expanded={isOpen}
        aria-controls={`qa-answer-${index}`}
        aria-label={`${item.question} - ${isOpen ? 'סגור תשובה' : 'פתח תשובה'}`}
        className="qa-question-btn w-full flex items-center gap-2 md:gap-3 text-right relative transition-all duration-300"
        style={{
          backgroundColor: 'transparent',
          outline: 'none',
          padding: window.innerWidth <= 768 ? '10px' : '16px',
        }}
        onMouseEnter={(e) => {
          if (e.currentTarget.parentElement && isVisible) {
            e.currentTarget.parentElement.style.boxShadow = `6px 6px 20px rgba(255, 255, 255, 0.2)`;
            e.currentTarget.parentElement.style.transform = 'translateX(0) translateY(-2px) rotate(0deg) scale(1)';
          }
        }}
        onMouseLeave={(e) => {
          if (e.currentTarget.parentElement && isVisible) {
            e.currentTarget.parentElement.style.boxShadow = `4px 4px 15px rgba(0, 0, 0, 0.3)`;
            e.currentTarget.parentElement.style.transform = 'translateX(0) translateY(0) rotate(0deg) scale(1)';
          }
        }}
      >
        {/* Plus/Minus Icon - + ו- */}
        <div 
          className="flex-shrink-0 flex items-center justify-center"
          style={{
            width: window.innerWidth <= 768 ? '24px' : '32px',
            height: window.innerWidth <= 768 ? '24px' : '32px',
            position: 'relative',
            backgroundColor: 'transparent',
            borderRadius: '50%',
            border: `2px solid ${colors.gold.base}`,
            transition: 'transform 0.3s ease',
          }}
        >
          {/* Plus/Minus - + או - */}
          <span
            style={{
              color: colors.gold.base,
              fontSize: window.innerWidth <= 768 ? '18px' : '24px',
              fontWeight: 'bold',
              lineHeight: '1',
              transition: 'transform 0.3s ease',
              transform: isOpen ? 'rotate(45deg)' : 'rotate(0deg)',
            }}
          >
            {isOpen ? '−' : '+'}
          </span>
        </div>

        {/* Question Text - gradient זהב-כתום על רקע שחור */}
        <div className="flex-1">
          <p
            id={`qa-question-${index}`}
            className="qa-question-text text-lg font-black leading-snug transition-all duration-300"
            style={{ 
              letterSpacing: typography.body.letterSpacing,
              background: colors.accentSpecial.textGradientRich,
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text',
              fontSize: 'clamp(0.9rem, 2.2vw, 1.3rem)',
              lineHeight: 'clamp(1.5, 1.4vw, 1.3)',
            }}
          >
            {item.question}
          </p>
        </div>
      </button>

      {/* Answer (when open) - עם slide down animation + spring effect */}
      {isOpen && (
        <div 
          id={`qa-answer-${index}`}
          role="region"
          aria-labelledby={`qa-question-${index}`}
          aria-live="polite"
          className="qa-answer px-4 pb-4 qa-answer-open"
          style={{ 
            paddingLeft: window.innerWidth <= 768 ? '32px' : '60px',
            paddingRight: window.innerWidth <= 768 ? '10px' : '16px',
            paddingTop: '8px',
            paddingBottom: window.innerWidth <= 768 ? '10px' : '16px',
            backgroundColor: '#FFFFFF',
            borderRadius: window.innerWidth <= 768 ? '0 0 14px 14px' : '0 0 22px 22px',
            animation: 'qaAnswerSlideDown 0.5s cubic-bezier(0.34, 1.56, 0.64, 1)',
          }}
        >
          <p 
            className="qa-answer-text text-base font-bold leading-normal" 
            style={{ 
              letterSpacing: typography.body.letterSpacing,
              color: '#000000',
              fontWeight: '700',
              fontSize: 'clamp(0.8rem, 1.85vw, 1.1rem)',
              lineHeight: 'clamp(1.6, 1.5vw, 1.4)',
              opacity: 0,
              animation: 'qaAnswerFadeIn 0.4s ease-out 0.2s forwards',
              wordBreak: 'keep-all',
              overflowWrap: 'break-word',
              hyphens: 'auto',
            }}
          >
            {item.answer}
          </p>
        </div>
      )}
    </div>
  );
};

export default QAItem;

